'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * One pointer stream, contained from press to release.
 *
 * Every draggable surface in the COP used to do this instead:
 *
 *     onPointerDown → window.addEventListener('pointermove' | 'pointerup')
 *
 * with no pointer capture, no preventDefault and no stopPropagation. That drag
 * only works because the events bubble to `window`, which means every element
 * the pointer passes over still receives the real pointerover/pointermove/
 * pointerup AND their compatibility mouseover/mousemove/mouseup. Dragging a
 * camera window across the map therefore lit up MapLibre's per-layer hover
 * handlers — cursor flip, hover chip, coordinate readout — and releasing fired a
 * `click` at the nearest common ancestor of the press and release targets, which
 * for a window nested inside another interactive container lands on a clickable
 * ancestor of the thing underneath.
 *
 * The pointer separates from the window routinely, so this was not an edge case:
 * a fast drag outruns the React re-render, and clamping the window at a viewport
 * edge pins it while the pointer keeps travelling.
 *
 * `setPointerCapture` is the fix. It retargets every subsequent event for THIS
 * pointer id to the handle, so nothing underneath sees the pointer events, the
 * compatibility mouse events, or the trailing click.
 *
 * It is deliberately NOT a modal. Capture is per-pointer and lives only from
 * press to release — there is no backdrop, no `pointer-events: none` on the app,
 * and no global listener while idle. With a window open the operator still
 * clicks drones, docks and the sidebar normally; the instant `pointerup` fires,
 * even the one captured pointer is released.
 */

export interface PointerDragDelta {
  /** Pixels travelled on X since the press. */
  dx: number;
  /** Pixels travelled on Y since the press. */
  dy: number;
}

export interface PointerDragOptions {
  /** Fired once, on a press that starts a drag. Capture the caller's start
   *  geometry here — `onDrag` reports deltas from the press point. */
  onDragStart?: (event: ReactPointerEvent<Element>) => void;
  onDrag: (delta: PointerDragDelta, event: PointerEvent) => void;
  /** `moved` is false when the pointer never passed `threshold` — i.e. this was
   *  a click, not a drag. */
  onDragEnd?: (moved: boolean) => void;
  /** Pixels of travel before it counts as a drag rather than a click. */
  threshold?: number;
  /** Cursor to force on <body> while dragging, e.g. 'ew-resize'. */
  cursor?: string;
}

/** Small enough that a deliberate drag always clears it, large enough that the
 *  hand tremor in a click never does. */
const DEFAULT_THRESHOLD = 3;

/**
 * <body> cursor/user-select are global, so the drags that borrow them have to be
 * counted rather than each saving and restoring its own snapshot.
 *
 * Two drags CAN overlap: a console with both a touchscreen and a mouse reports
 * both as primary pointers, so a finger resizing the dock sheet and a mouse
 * dragging a window are two live drags in two hook instances. With per-instance
 * snapshots, whichever finished last would restore the value it captured from
 * the other — leaving `user-select: none` on <body> for the rest of the session,
 * so no callsign or telemetry value could be selected or copied.
 */
let activeDragCount = 0;
let savedCursor = '';
let savedUserSelect = '';

function acquireBodyStyles(cursor?: string) {
  if (activeDragCount === 0) {
    savedCursor = document.body.style.cursor;
    savedUserSelect = document.body.style.userSelect;
  }
  activeDragCount += 1;
  if (cursor) document.body.style.cursor = cursor;
  document.body.style.userSelect = 'none';
}

function releaseBodyStyles() {
  activeDragCount = Math.max(0, activeDragCount - 1);
  if (activeDragCount === 0) {
    document.body.style.cursor = savedCursor;
    document.body.style.userSelect = savedUserSelect;
  }
}

interface ActiveDrag {
  /** Detach listeners and release capture. Used on unmount. */
  teardown: () => void;
  /** Teardown, plus the trailing-click suppression and onDragEnd that a normal
   *  release would have run. Used when a new press supersedes this drag. */
  abandon: () => void;
}

export function usePointerDrag({
  onDragStart,
  onDrag,
  onDragEnd,
  threshold = DEFAULT_THRESHOLD,
  cursor,
}: PointerDragOptions) {
  // The callbacks are re-created on every render by most callers; reading them
  // through a ref keeps the pointerdown handler stable and, more importantly,
  // keeps an in-flight drag pointed at the current closure rather than the one
  // that happened to be live when the press landed.
  const callbacks = useRef({ onDragStart, onDrag, onDragEnd });
  // After every render, not during it — and safely so, because nothing reads
  // these outside a pointer handler, which cannot fire before the paint that
  // put the handle on screen.
  useEffect(() => {
    callbacks.current = { onDragStart, onDrag, onDragEnd };
  });

  const activeDrag = useRef<ActiveDrag | null>(null);

  // A drag can outlive its owner: the COP camera window unmounts the moment its
  // aircraft stops being selected, which happens mid-drag on a telemetry drop or
  // a site-filter change. Without this the listeners would keep running against
  // a dead tree, and the body cursor would stay stuck.
  useEffect(() => () => activeDrag.current?.teardown(), []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<Element>) => {
      // Secondary buttons belong to the context menu, and a non-primary pointer
      // is a second finger — neither starts a drag.
      if (!event.isPrimary || event.button !== 0) return;

      // Never run two drags at once in one instance. Abandon rather than bail:
      // on the no-capture fallback a pointer released outside the browser never
      // delivers pointerup, and a guard that simply returned here would wedge
      // the handle permanently — every later drag refused by one that already
      // ended. Going through `abandon` (not a bare teardown) means the stale
      // drag still suppresses its trailing click and still reports onDragEnd,
      // so a caller's cleanup is never silently skipped.
      activeDrag.current?.abandon();

      // Stops the native text selection and drag-image that the header text
      // would otherwise start.
      event.preventDefault();
      // The press must not reach DOM ancestors. This is what protects a window
      // rendered inside another interactive container.
      event.stopPropagation();

      const handle = event.currentTarget;
      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;

      // A browser can refuse capture for a pointer that is already gone, and a
      // test environment may not implement it at all. Either way we fall back to
      // window listeners — the old behaviour — and the trailing-click suppressor
      // is what keeps that path honest.
      let captured = false;
      try {
        if (typeof handle.setPointerCapture === 'function') {
          handle.setPointerCapture(pointerId);
          captured = true;
        }
      } catch {
        captured = false;
      }

      // With capture held, the events are retargeted to the handle, so listening
      // on it is enough. Without it, only `window` sees the whole gesture.
      const target: EventTarget = captured ? handle : window;

      acquireBodyStyles(cursor);

      let moved = false;
      let torndown = false;

      const handleMove = (moveEvent: Event) => {
        const pointerEvent = moveEvent as PointerEvent;
        if (pointerEvent.pointerId !== pointerId) return;
        const dx = pointerEvent.clientX - startX;
        const dy = pointerEvent.clientY - startY;
        if (!moved && Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
        moved = true;
        callbacks.current.onDrag({ dx, dy }, pointerEvent);
      };

      const teardown = () => {
        // Idempotent: abandon() and the unmount cleanup can both reach this, and
        // double-releasing would unbalance the body-style count.
        if (torndown) return;
        torndown = true;
        target.removeEventListener('pointermove', handleMove);
        target.removeEventListener('pointerup', finish);
        // A touch drag the browser reclaims for scrolling fires pointercancel
        // and never pointerup; without it the window would follow the next touch.
        target.removeEventListener('pointercancel', finish);
        target.removeEventListener('lostpointercapture', finish);
        releaseBodyStyles();
        if (captured) {
          try {
            handle.releasePointerCapture(pointerId);
          } catch {
            // Already released — the pointer left the document, or the browser
            // revoked capture and fired lostpointercapture to say so.
          }
        }
        activeDrag.current = null;
      };

      const end = () => {
        teardown();
        // A drag that travelled must not also read as a click. Without this the
        // browser still fires one at the end of the gesture: on the no-capture
        // fallback it lands on the common ancestor of press and release, and
        // even under capture a drag that happens to end on an interactive
        // element would activate it.
        if (moved) suppressNextClick();
        callbacks.current.onDragEnd?.(moved);
      };

      function finish(finishEvent: Event) {
        // A second finger's pointerup must not end this drag.
        if ((finishEvent as PointerEvent).pointerId !== pointerId) return;
        end();
      }

      activeDrag.current = { teardown, abandon: end };
      target.addEventListener('pointermove', handleMove);
      target.addEventListener('pointerup', finish);
      target.addEventListener('pointercancel', finish);
      // The browser can revoke capture on its own (the handle being removed,
      // the pointer being cancelled). Treat it as the end of the drag.
      target.addEventListener('lostpointercapture', finish);

      callbacks.current.onDragStart?.(event);
    },
    [cursor, threshold],
  );

  return { onPointerDown };
}

/**
 * Swallow exactly one click, and only if the browser is about to fire one.
 *
 * Capture phase so it runs before any application handler. Three ways to
 * disarm, because a suppressor that outlives its gesture would eat a click the
 * operator meant:
 *
 *  - the click arrives and is swallowed — the expected path;
 *  - the next macrotask runs, meaning the browser fired no click at all;
 *  - a new pointerdown arrives, which starts a fresh interaction and proves the
 *    old gesture's click is never coming.
 */
function suppressNextClick() {
  const suppress = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
    disarm();
  };
  const disarm = () => {
    window.clearTimeout(timer);
    document.removeEventListener('click', suppress, true);
    document.removeEventListener('pointerdown', disarm, true);
  };
  const timer = window.setTimeout(disarm, 0);
  document.addEventListener('click', suppress, true);
  document.addEventListener('pointerdown', disarm, true);
}
